import { useEffect, useState, useMemo, useRef } from 'react';
import usePermission from 'hooks/permission';
import * as all from 'constants/permissions';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  getAllBusinessesByLimit,
  getMoreBusinessesByLimit,
} from 'redux/actions/mev3/meV3';
import {
  selectBrandList,
  selectCurrentBusiness,
  selectCurrentSelectedBrand,
  selectCurrentSelectedLocation,
  selectFetchMoreDataLoading,
  selectIsBrandLoading,
  selectLocationList,
  selectPagination,
  selectShowOnlyActiveBusinesses,
} from 'redux/selectors';
import { getCurrentBusiness } from 'redux/actions/business';

const useBusinessDropDown = () => {
  //redux
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [isShowOnlyLoading, setIsShowOnlyLoading] = useState(false);

  //hooks
  const checkPermission = usePermission();
  const childContainerRef = useRef(null);

  //selectors
  const brandList = useSelector(selectBrandList);
  const pagination = useSelector(selectPagination);
  const locationList = useSelector(selectLocationList);
  const isBrandLoading = useSelector(selectIsBrandLoading);
  const currentBusiness = useSelector(selectCurrentBusiness);
  const currentSelectedBrand = useSelector(selectCurrentSelectedBrand);
  const fetchMoreDataLoading = useSelector(selectFetchMoreDataLoading);
  const currentSelectedLocation = useSelector(selectCurrentSelectedLocation);
  const showOnlyActiveBusinesses = useSelector(selectShowOnlyActiveBusinesses);

  //state
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  useEffect(() => {
    dispatch(getAllBusinessesByLimit(10, 1, ''));
  }, []);

  const dropdownBusinessName = useMemo(() => {
    if (currentBusiness && Object.keys(currentBusiness).length > 0) {
      return `${currentBusiness?.brandInfo?.brandName}  ${
        currentBusiness?.addressInfo?.address1
          ? `- ${currentBusiness?.addressInfo?.address1}`
          : ''
      }`;
    }
    return 'All Businesses';
  }, [currentBusiness?._id]);

  const fetchMoreData = () => {
    const isActive =
      localStorage.getItem('showActiveBusiness') === null
        ? true
        : Boolean(Number(localStorage.getItem('showActiveBusiness')));
    dispatch(
      getMoreBusinessesByLimit(
        pagination.limit,
        pagination.nextPage,
        '',
        isActive
      )
    );
  };

  const selectCurrentBrand = (item) => {
    dispatch({ type: 'SET_CURRENT_BRAND_MEV3', payload: item });
  };

  useEffect(() => {
    if (open && id === 'simple-popover') {
      dispatch({ type: 'SET_CURRENT_BRAND_MEV3', payload: brandList[0] });
    }
  }, [open, id]);

  const formatSelectedLocation = () => {
    return {
      classifications: currentSelectedLocation?.classifications,
      disabled: currentSelectedLocation?.disabled,
      label:
        currentSelectedLocation?.locationName || currentSelectedLocation?.label,
      name:
        currentSelectedLocation?.locationName || currentSelectedLocation?.name,
      value: {
        _id: currentSelectedLocation?._id,
        locationName: currentSelectedLocation?.locationName,
        businessId: currentSelectedLocation?.businessId,
        ...currentSelectedLocation?.value,
      },
      verified: currentSelectedLocation?.verified,
    };
  };

  const selectedBrandFormat = () => {
    return {
      id: currentSelectedBrand?._id,
      label: currentSelectedBrand?.brandName,
      name: currentSelectedBrand?.brandName,
      value: {
        _id: currentSelectedBrand?._id,
        brandName: currentSelectedBrand?.brandName,
        locations: currentSelectedBrand?.locations || [],
      },
    };
  };

  const handleSubmit = () => {
    localStorage.removeItem('alreadyLoaded');
    localStorage.removeItem('currentTab');
    history.push(history.location.pathname);
    if (currentSelectedLocation && currentSelectedLocation?.businessId) {
      // Set selected location in the specified format
      const selectedLocation = formatSelectedLocation();
      const selectedBrand = selectedBrandFormat();
      localStorage.setItem(
        'selectedLocation',
        JSON.stringify(selectedLocation)
      );
      localStorage.setItem('selectedBrand', JSON.stringify(selectedBrand));

      dispatch(getCurrentBusiness(currentSelectedLocation?.businessId));
      localStorage.setItem(
        'businessInfo',
        JSON.stringify({
          label: ``,
          id: currentSelectedLocation?.businessId,
        })
      );
      setAnchorEl(null);
      handleClose();
      if (
        location.pathname.includes('/providers') &&
        location.pathname.includes('settings')
      ) {
        history.push('/providers');
      }

      const handleSaveData = async () => {
        const confirmed = confirm({
          hideCloseIcon: false,
          hideDiscardBtn: false,
          onClose: () => {},
        });
        if (!confirmed) {
          if (document.getElementById('productSaveButton')) {
            dispatch({ type: 'FORM_VALIDITY', payload: true });
            return document.getElementById('productSaveButton').click();
          }
          if (document.getElementById('saveButton')) {
            dispatch({ type: 'FORM_VALIDITY', payload: true });
            return document.getElementById('saveButton').click();
          }
        } else {
          if (
            location.pathname.includes('menu') &&
            location.pathname.includes('main')
          ) {
            window.location.href = '/menu/main';
          } else {
            window.location.reload();
          }
        }
      };
      if (
        !(
          (location.pathname.includes('menu') &&
            location.pathname.includes('edit')) ||
          location.pathname.includes('add-category') ||
          location.pathname.includes('add-product')
        ) ||
        location.pathname.includes('doordash') ||
        location.pathname.includes('website') ||
        location.pathname.includes('grubhub')
      ) {
        if (
          location.pathname.includes('menu') &&
          location.pathname.includes('main')
        ) {
          window.location.href = '/menu/main';
        } else {
          window.location.reload();
        }
      } else {
        if (currentBusiness._id) {
          handleSaveData();
        }
      }
      if (location.pathname.includes('schedule/new')) {
        window.location.href = '/schedule/';
      }
    }
    if (location.pathname.includes('menu')) {
      window.location.href = window.location.pathname;
    }
  };

  const handleShowOnlyActiveBusinesses = (
    currentSearchValue,
    isCurrentlySearching,
    retriggerSearchCallback
  ) => {
    setIsShowOnlyLoading(true);
    dispatch({
      type: 'SET_SHOW_ONLY_ACTIVE_BUSINESSES',
      payload: !showOnlyActiveBusinesses,
    });
    localStorage.setItem(
      'showActiveBusiness',
      !showOnlyActiveBusinesses ? '1' : '0'
    );

    // If there's an active search, re-trigger it with the new filter
    if (
      isCurrentlySearching &&
      currentSearchValue?.trim() &&
      retriggerSearchCallback
    ) {
      // Small delay to ensure localStorage is updated before search
      setTimeout(() => {
        retriggerSearchCallback();
        setIsShowOnlyLoading(false);
      }, 100);
    } else {
      // Otherwise, fetch regular business list
      dispatch(
        getAllBusinessesByLimit(10, 1, '', !showOnlyActiveBusinesses, false)
      );
      setTimeout(() => {
        setIsShowOnlyLoading(false);
      }, 1000);
    }
  };

  return {
    anchorEl,
    open,
    id,
    handleOpen,
    handleClose,
    checkPermission,
    dispatch,
    location,
    history,
    all,
    currentBusiness,
    isBrandLoading,
    currentSelectedBrand,
    brandList,
    pagination,
    dropdownBusinessName,
    fetchMoreData,
    fetchMoreDataLoading,
    selectCurrentBrand,
    locationList,
    currentSelectedLocation,
    handleSubmit,
    showOnlyActiveBusinesses,
    handleShowOnlyActiveBusinesses,
    childContainerRef,
    isShowOnlyLoading,
  };
};

export default useBusinessDropDown;
